import React, { useState, useEffect, useCallback, useMemo, useContext, useRef } from "react";
import { useLocation } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Board from "react-trello";
import {
  Badge,
  Tooltip,
  Typography,
  Chip,
  Box,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ButtonGroup,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
} from "@material-ui/core";
import { AttachFile as AttachFileIcon } from "@material-ui/icons";
import { Facebook, Instagram, WhatsApp, OfflineBolt, MoreVert, Settings as SettingsIcon, Add as AddIcon, Event as EventIcon, AttachFile, Send as SendIcon, Assignment as AssignmentIcon, ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon } from "@material-ui/icons";
import { toast } from "react-toastify";
import moment from "moment";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import BoardSettingsModal from "../../components/BoardSettingsModal";
import BoardCardModal from "../../components/BoardCardModal";
import AppointmentModal from "../../components/AppointmentModal";
import ScheduleModal from "../../components/ScheduleModal";
import CardFieldInput from "../../components/CardFieldInput";
import { Can } from "../../components/Can";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(1),
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    width: "100%",
    maxWidth: "1300px",
  },
  toolbarLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  boardContainer: {
    width: "100%",
    maxWidth: "1300px",
    margin: "0 auto",
  },
  connectionTag: {
    background: "green",
    color: "#FFF",
    marginRight: 1,
    padding: 1,
    fontWeight: "bold",
    borderRadius: 3,
    fontSize: "0.6em",
  },
  laneTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  fieldChip: {
    marginRight: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    height: 20,
    fontSize: "0.7em",
  },
  cardRoot: {
    background: "#fff",
    borderRadius: 4,
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    cursor: "pointer",
    boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
    // react-trello aplica "white-space: nowrap" globalmente (pensado
    // pros textos curtos do Card padrao dele) - sem isso, mensagens
    // reais de ticket (mais longas) esticam a fase inteira em vez de
    // quebrar linha.
    whiteSpace: "normal",
    wordBreak: "break-word",
    width: "100%",
    boxSizing: "border-box",
  },
  cardTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  cardNumber: {
    fontSize: "0.75em",
    color: theme.palette.text.secondary,
  },
  cardLastMessage: {
    fontSize: "0.8em",
    margin: theme.spacing(0.5, 0),
  },
  // Kanban mobile: o drag-and-drop do react-trello (biblioteca smooth-dnd)
  // intercepta o toque pra detectar arrasto e acaba bloqueando o gesto
  // nativo de deslizar a tela em celulares - bug real reportado pelo
  // usuario 2026-09-19 ("nao consigo mover pro lado pra verificar os
  // quadros", confirmado no mobile). Em vez de remendar a lib de
  // terceiros, abaixo do breakpoint "sm" o board vira abas de fase +
  // lista (sem arrastar nada) - mesma ideia aprovada no mockup mostrado
  // ao usuario antes de implementar.
  mobilePhaseTabs: {
    display: "flex",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1),
  },
  mobilePhaseChip: {
    flex: "none",
    fontWeight: 700,
  },
  mobileCardRow: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: theme.spacing(1.25, 1.5),
    marginBottom: theme.spacing(1),
    cursor: "pointer",
  },
}));

const IconChannel = (channel) => {
  switch (channel) {
    case "facebook":
      return <Facebook style={{ color: "#3b5998", verticalAlign: "middle", fontSize: "16px" }} />;
    case "instagram":
      return <Instagram style={{ color: "#e1306c", verticalAlign: "middle", fontSize: "16px" }} />;
    case "whatsapp":
      return <WhatsApp style={{ color: "#25d366", verticalAlign: "middle", fontSize: "16px" }} />;
    default:
      return null;
  }
};

// Cabecalho customizado da fase (nome + botao "..." de configuracoes).
// Fix 2026-09-28: antes esse JSX (nome + IconButton) vivia direto no
// campo "title" que a gente monta pra cada lane (ver boardData abaixo).
// title FUNCIONAVA visualmente (o LaneHeader padrao do react-trello so'
// usa {title} como filho, aceita qualquer node), mas o Lane.js da
// biblioteca TAMBEM espalha esse mesmo "title" cru como atributo nativo
// da <section> por baixo (via {...otherProps}, sem filtrar) - um
// elemento React virando o atributo HTML "title" faz o navegador
// converter pra string sozinho, aparecendo "[object Object]" no hover
// de QUALQUER fase (bug real reportado 2026-09-28: "no card quando
// coloca o mouse encima so mostra object object" - era a fase, nao o
// card). Agora "title" fica como STRING pura (seguro pro atributo
// nativo) e o JSX customizado vira este componente separado, plugado
// via components={{ LaneHeader: MyLaneHeader }} no <Board> - mesma
// tecnica que MyCard ja usava pelo mesmo motivo, so' que pra fase.
const MyLaneHeader = ({ title, label, onMenuClick }) => {
  const classes = useStyles();
  return (
    <div className={classes.laneTitle}>
      <span>{title}</span>
      <IconButton size="small" onClick={onMenuClick}>
        <MoreVert fontSize="small" />
      </IconButton>
    </div>
  );
};

// Card customizado - o Card padrao do react-trello so aceita
// title/description como string (ver BoardMockup, mesma descoberta) -
// usa "onClick" (ja vem de Lane.js) pra abrir o BoardCardModal.
// title: nome do card avulso (sem ticket) - pedido do usuario 2026-08-27.
// attachmentCount: so pra mostrar o clipe no card sem precisar abrir -
// os anexos de verdade (baixar/excluir) ficam no BoardCardModal.
const TEMPERATURE_STYLE = {
  quente: { label: "Quente", color: "#c62828", background: "#fdecea" },
  morno: { label: "Morno", color: "#e65100", background: "#fff3e0" },
  frio: { label: "Frio", color: "#1565c0", background: "#e3f2fd" },
};

// title/fieldValues foram trocados por "summary" e "temperature" no card
// visivel (pedido do usuario 2026-08-28: "olha como ficou o card todo
// cagado, era pra ter apenas um resumo da ia" - antes empilhava um chip
// por campo preenchido, ficava enorme com muitos campos). Os campos
// individuais continuam disponiveis dentro do BoardCardModal, so' saem
// da visao do board. Design inspirado no card do Base44 (KanbanCard.jsx
// do projeto de referencia): titulo + caixa de resumo destacada,
// truncada em poucas linhas, sem lista de campos crus.
const MyCard = ({ id, ticket, title, summary, temperature, responsavel, attachmentCount, taskSummary, appointment, onSchedule, onScheduleMessage, onClick, style }) => {
  const classes = useStyles();
  const contact = ticket?.contact;
  const unread = Number(ticket?.unreadMessages) || 0;
  const displayName = contact?.name || title || "(sem nome)";
  const tempStyle = temperature && TEMPERATURE_STYLE[temperature];

  return (
    <div className={classes.cardRoot} style={style} onClick={onClick}>
      <div className={classes.cardTopRow}>
        <span>
          {/* Tooltip do MUI clona o filho e exige um elemento valido - sem
              o span, um card sem ticket.channel (IconChannel retornando
              null) quebrava o render inteiro (ver comentario no <Board>
              acima). */}
          <Tooltip title={ticket?.channel || ""}><span>{IconChannel(ticket?.channel)}</span></Tooltip> <b>{displayName}</b>
          {tempStyle && (
            <span
              style={{
                marginLeft: 6,
                fontSize: "0.65em",
                fontWeight: "bold",
                color: tempStyle.color,
                background: tempStyle.background,
                borderRadius: 4,
                padding: "1px 5px",
                verticalAlign: "middle",
              }}
            >
              {tempStyle.label}
            </span>
          )}
        </span>
        <span>
          {unread > 0 && <Badge badgeContent={unread} color="error" style={{ marginRight: 8 }} />}
          {attachmentCount > 0 && (
            <Tooltip title={`${attachmentCount} anexo(s)`}>
              <AttachFile fontSize="small" style={{ verticalAlign: "middle", marginRight: 4, color: "rgba(0,0,0,0.4)" }} />
            </Tooltip>
          )}
          {taskSummary && taskSummary.total > 0 && (
            <Tooltip title={taskSummary.overdue ? "Tem tarefa atrasada" : `${taskSummary.done}/${taskSummary.total} tarefas`}>
              <span style={{ display: "inline-flex", alignItems: "center", marginRight: 4 }}>
                <AssignmentIcon
                  fontSize="small"
                  style={{ verticalAlign: "middle", color: taskSummary.overdue ? "#c62828" : "rgba(0,0,0,0.4)" }}
                />
                <span style={{ fontSize: "0.65em", marginLeft: 2, color: taskSummary.overdue ? "#c62828" : "rgba(0,0,0,0.5)", fontWeight: taskSummary.overdue ? "bold" : "normal" }}>
                  {taskSummary.done}/{taskSummary.total}
                </span>
              </span>
            </Tooltip>
          )}
          {/* Compromisso funciona com ou sem ticket - Appointment.contactId
              e ticketId sao nullable no banco, entao card avulso tambem
              pode agendar (pedido do usuario 2026-08-28, ele nao achava
              essa opcao no card avulso porque eu tinha escondido o botao
              aqui por engano, achando que precisava de ticket). */}
          <Tooltip title={appointment ? "Ver/editar compromisso" : "Agendar compromisso"}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onSchedule();
              }}
            >
              <EventIcon fontSize="small" color={appointment ? "primary" : "disabled"} />
            </IconButton>
          </Tooltip>
          {/* Agendamento de MENSAGEM (envio futuro de WhatsApp) e' um
              sistema separado do compromisso/visita acima - pedido do
              usuario 2026-08-28 ("tenho que ter tanto o agendamento local
              quanto o agendamento de mensagem"). Reaproveita o
              ScheduleModal inteiro (ja tem busca de contato e escolha de
              conexao prontos). */}
          <Tooltip title="Agendar mensagem">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onScheduleMessage();
              }}
            >
              <SendIcon fontSize="small" color="disabled" />
            </IconButton>
          </Tooltip>
        </span>
      </div>
      {contact?.number && <div className={classes.cardNumber}>{contact.number}</div>}
      {/* Resumo do atendimento (gerado pela IA) em vez da ultima
          mensagem crua - pedido do usuario 2026-08-27, a ultima
          mensagem isolada raramente da contexto suficiente pra
          entender o card so olhando o board. */}
      <div className={classes.cardLastMessage}>
        {ticket?.liveSummary || ticket?.closingSummary || ticket?.lastMessage || " "}
      </div>
      {appointment && (
        <Chip
          size="small"
          icon={<EventIcon />}
          label={moment(appointment.startAt).format("DD/MM HH:mm")}
          className={classes.fieldChip}
          color="primary"
          variant="outlined"
        />
      )}
      {summary && (
        <div
          style={{
            marginTop: 6,
            background: "#eef4fb",
            border: "1px solid #cfe0f2",
            borderRadius: 4,
            padding: "4px 6px",
            fontSize: "0.75em",
            color: "#1b3a5b",
            lineHeight: 1.3,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {summary}
        </div>
      )}
      {ticket?.user && (
        <Box mt={0.5}>
          <Badge style={{ backgroundColor: "#000000" }} className={classes.connectionTag}>
            {ticket.user.name.toUpperCase()}
          </Badge>
        </Box>
      )}
      {responsavel && (
        <Box mt={0.5}>
          <Chip size="small" label={responsavel.name} className={classes.fieldChip} variant="outlined" />
        </Box>
      )}
    </div>
  );
};

// Linha de lista pro Kanban mobile (ver comentario em "mobilePhaseTabs"
// acima) - mesma informacao do MyCard, so' sem depender do slot de Card
// do react-trello (que nao existe fora do board de arrastar). Mover de
// fase acontece dentro do BoardCardModal (que ja tem esse seletor
// pronto), aberto pelo mesmo onClick que o board de desktop usa.
const MobileCardRow = ({ card, onClick, onSchedule, onScheduleMessage }) => {
  const classes = useStyles();
  const ticket = card.ticket;
  const contact = ticket?.contact;
  const unread = Number(ticket?.unreadMessages) || 0;
  const displayName = contact?.name || card.title || "(sem nome)";
  const tempStyle = card.temperature && TEMPERATURE_STYLE[card.temperature];
  const lastText = card.summary || ticket?.liveSummary || ticket?.closingSummary || ticket?.lastMessage;

  return (
    <div className={classes.mobileCardRow} onClick={onClick}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box minWidth={0}>
          <Typography variant="body2" style={{ fontWeight: 700 }} noWrap>
            {IconChannel(ticket?.channel)} {displayName}
          </Typography>
          {contact?.number && (
            <Typography variant="caption" color="textSecondary" display="block">
              {contact.number}
            </Typography>
          )}
        </Box>
        <Box display="flex" alignItems="center" flexShrink={0}>
          {unread > 0 && <Badge badgeContent={unread} color="error" style={{ marginRight: 8 }} />}
          {tempStyle && (
            <span
              style={{
                fontSize: "0.7em",
                fontWeight: "bold",
                color: tempStyle.color,
                background: tempStyle.background,
                borderRadius: 4,
                padding: "1px 6px",
                whiteSpace: "nowrap",
              }}
            >
              {tempStyle.label}
            </span>
          )}
        </Box>
      </Box>
      {lastText && (
        <Typography
          variant="caption"
          color="textSecondary"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            marginTop: 4,
          }}
        >
          {lastText}
        </Typography>
      )}
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
        <Box>
          {card.responsavel && <Chip size="small" label={card.responsavel.name} variant="outlined" />}
        </Box>
        <Box>
          <Tooltip title={card.nextAppointment ? "Ver/editar compromisso" : "Agendar compromisso"}>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onSchedule(); }}>
              <EventIcon fontSize="small" color={card.nextAppointment ? "primary" : "disabled"} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Agendar mensagem">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onScheduleMessage(); }}>
              <SendIcon fontSize="small" color="disabled" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </div>
  );
};

const Boards = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user, socket } = useContext(AuthContext);
  const location = useLocation();
  const deepLinkCardConsumed = useRef(null);
  const [boards, setBoards] = useState([]);
  const [activeBoardId, setActiveBoardId] = useState(null);
  const [phases, setPhases] = useState([]);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState([]);
  const [cards, setCards] = useState([]);
  const [rulesCount, setRulesCount] = useState(0);
  const [rules, setRules] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [newCardMode, setNewCardMode] = useState("ticket"); // "ticket" | "standalone"
  const [newCardTicketId, setNewCardTicketId] = useState("");
  const [newCardTitle, setNewCardTitle] = useState("");
  const [newCardPhaseId, setNewCardPhaseId] = useState("");
  const [newCardResponsavelId, setNewCardResponsavelId] = useState("");
  const [newCardFieldValues, setNewCardFieldValues] = useState({});
  const [users, setUsers] = useState([]);
  const [newCardFiles, setNewCardFiles] = useState([]);
  const [savingNewCard, setSavingNewCard] = useState(false);
  const [newBoardOpen, setNewBoardOpen] = useState(false);
  const [newBoardName, setNewBoardName] = useState("");
  const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [appointmentCard, setAppointmentCard] = useState(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleCard, setScheduleCard] = useState(null);
  const [activeMobilePhaseId, setActiveMobilePhaseId] = useState(null);

  const fetchBoards = useCallback(async () => {
    try {
      const { data } = await api.get("/boards");
      setBoards(data);
      if (data.length > 0 && !activeBoardId) {
        setActiveBoardId(data[0].id);
      }
    } catch (err) {
      toastError(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Depois de excluir o board ativo, limpa o id atual pra fetchBoards
  // cair na condicao "!activeBoardId" e escolher o primeiro board que
  // sobrou sozinho, em vez de continuar tentando mostrar um board que
  // nao existe mais.
  const handleBoardDeleted = async () => {
    setActiveBoardId(null);
    await fetchBoards();
  };

  // Reordenar boards (setinhas ao lado do Select) - pedido do usuario
  // 2026-09-28 ("preciso poder mudar cada board de posicao, exemplo
  // pendencias tem que ficar no ultimo"). Troca so' com o vizinho
  // imediato (sobe/desce um lugar por clique) - mais simples que
  // arrastar-e-soltar e nao precisa de biblioteca nova. Otimista: atualiza
  // a tela na hora, desfaz e recarrega se a API falhar.
  const handleMoveBoardPosition = async (direction) => {
    const idx = boards.findIndex((b) => b.id === activeBoardId);
    if (idx === -1) return;
    const swapWith = direction === "up" ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= boards.length) return;

    const reordered = [...boards];
    [reordered[idx], reordered[swapWith]] = [reordered[swapWith], reordered[idx]];
    setBoards(reordered);

    try {
      await api.post("/boards/reorder", { orderedBoardIds: reordered.map((b) => b.id) });
    } catch (err) {
      toastError(err);
      fetchBoards();
    }
  };

  const fetchBoardDetail = useCallback(async () => {
    if (!activeBoardId) return;
    try {
      const [boardRes, cardsRes, rulesRes] = await Promise.all([
        api.get(`/boards/${activeBoardId}`),
        api.get("/cards", { params: { boardId: activeBoardId } }),
        api.get("/movement-rules", { params: { boardId: activeBoardId } }),
      ]);
      setPhases(boardRes.data.phases || []);
      setCustomFieldDefinitions(boardRes.data.customFieldDefinitions || []);
      setCards(cardsRes.data || []);
      setRules(rulesRes.data || []);
      setRulesCount((rulesRes.data || []).length);
    } catch (err) {
      toastError(err);
    }
  }, [activeBoardId]);

  useEffect(() => {
    fetchBoards();
  }, [fetchBoards]);

  useEffect(() => {
    fetchBoardDetail();
  }, [fetchBoardDetail]);

  // Lista de usuarios pro select de "Responsavel" no card avulso - mesma
  // fonte que o BoardCardModal ja usa pra editar depois de criado.
  useEffect(() => {
    api
      .get("/users/")
      .then(({ data }) => setUsers(data.users || []))
      .catch(() => {});
  }, []);

  // Deep link vindo do popover "Minhas Tarefas" ou da pagina "/tasks"
  // (?board=X&card=Y) - troca o board ativo mesmo se o usuario ja
  // estiver na tela de Boards olhando outro board, pois um history.push
  // pra mesma rota nao remonta o componente.
  useEffect(() => {
    if (boards.length === 0) return;
    const params = new URLSearchParams(location.search);
    const boardParam = Number(params.get("board"));
    if (boardParam && boardParam !== activeBoardId && boards.some((b) => b.id === boardParam)) {
      setActiveBoardId(boardParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, boards]);

  useEffect(() => {
    if (cards.length === 0) return;
    const params = new URLSearchParams(location.search);
    const cardParam = params.get("card");
    if (!cardParam) return;
    if (deepLinkCardConsumed.current === location.search) return;
    const card = cards.find((c) => String(c.id) === String(cardParam));
    if (!card) return;
    deepLinkCardConsumed.current = location.search;
    setSelectedCard(card);
    setCardModalOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, location.search]);

  // Sync em tempo real entre usuarios diferentes olhando o mesmo board -
  // antes so atualizava com F5. O backend ja emitia esses eventos
  // (CardController/CardAttachmentController) mas com namespace/nome de
  // evento errados, entao nunca chegavam em lugar nenhum; corrigido junto
  // com este listener, 2026-08-28. Debounce evita recarregar 3x quando
  // varios eventos chegam juntos (ex: criar card com anexo).
  useEffect(() => {
    if (!socket || !user?.companyId || !activeBoardId) return;

    let debounceTimer = null;
    const scheduleRefresh = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        fetchBoardDetail();
      }, 400);
    };

    const eventName = `company-${user.companyId}-card`;
    socket.on(eventName, scheduleRefresh);

    return () => {
      clearTimeout(debounceTimer);
      socket.off(eventName, scheduleRefresh);
    };
  }, [socket, user, activeBoardId, fetchBoardDetail]);

  const handleLaneMenuClick = () => {
    setSettingsOpen(true);
  };

  const boardData = useMemo(() => {
    const lanes = phases.map((phase) => {
      const phaseCards = cards
        .filter((c) => c.phaseId === phase.id)
        .sort((a, b) => (a.position || 0) - (b.position || 0));

      return {
        id: String(phase.id),
        // String pura de proposito - ver comentario de MyLaneHeader (o
        // JSX do nome+botao "..." mudou pra la'), react-trello espalha
        // este campo cru como atributo nativo da <section> por baixo.
        title: phase.name,
        onMenuClick: handleLaneMenuClick,
        label: phaseCards.length.toString(),
        style: {
          backgroundColor: `${phase.color || "#9e9e9e"}22`,
          borderTop: `3px solid ${phase.color || "#9e9e9e"}`,
          // Sem isso a fase estica pra caber o conteudo do card
          // (mensagem do ticket) em vez de manter coluna fixa.
          width: 280,
          minWidth: 280,
          maxWidth: 280,
        },
        cards: phaseCards.map((card) => {
          const tasks = card.tasks || [];
          const now = new Date();
          return {
            id: String(card.id),
            draggable: true,
            ticket: card.ticket,
            title: card.title,
            summary: card.summary,
            temperature: card.temperature,
            responsavel: card.responsavel,
            attachmentCount: (card.attachments || []).length,
            taskSummary: tasks.length
              ? {
                  total: tasks.length,
                  done: tasks.filter((t) => t.done).length,
                  overdue: tasks.some((t) => !t.done && t.dueDate && new Date(t.dueDate) < now),
                }
              : null,
            appointment: card.nextAppointment || null,
            onSchedule: () => handleOpenAppointment(card),
            onScheduleMessage: () => handleOpenScheduleMessage(card),
          };
        }),
      };
    });

    return { lanes };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phases, cards]);

  // Fase ativa da visao mobile (abas em vez de colunas) - sempre cai na
  // primeira fase quando o board carrega ou muda, e nao trava numa fase
  // que deixou de existir se o usuario editar as fases.
  useEffect(() => {
    if (phases.length === 0) return;
    if (!phases.some((p) => p.id === activeMobilePhaseId)) {
      setActiveMobilePhaseId(phases[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phases]);

  const mobilePhaseCards = useMemo(
    () =>
      cards
        .filter((c) => c.phaseId === activeMobilePhaseId)
        .sort((a, b) => (a.position || 0) - (b.position || 0)),
    [cards, activeMobilePhaseId]
  );

  const handleCardMove = async (sourceLaneId, targetLaneId, cardId, index) => {
    try {
      await api.put(`/cards/${cardId}/move`, {
        targetPhaseId: Number(targetLaneId),
        targetPosition: index,
      });
      fetchBoardDetail();
    } catch (err) {
      toastError(err);
      fetchBoardDetail();
    }
  };

  const handleCardClick = (cardId) => {
    const card = cards.find((c) => String(c.id) === String(cardId));
    if (!card) return;
    setSelectedCard(card);
    setCardModalOpen(true);
  };

  // selectedCard e' so' o ID de qual card esta aberto - o CONTEUDO exibido
  // no modal tem que vir sempre do array "cards" ao vivo, senao toggle de
  // tarefa/exclusao/anexo ficam "presos" no estado de quando o modal
  // abriu, so' atualizando depois de fechar+reabrir ou dar F5 (bug real
  // reportado pelo usuario 2026-08-29: "nao consigo tikar para concluir",
  // "a tarefa so some depois de dar f5").
  const modalCard = selectedCard
    ? cards.find((c) => c.id === selectedCard.id) || selectedCard
    : null;

  const handleOpenAppointment = (card) => {
    setAppointmentCard(card);
    setSelectedAppointment(card.nextAppointment || null);
    setAppointmentModalOpen(true);
  };

  const handleCloseAppointmentModal = () => {
    setAppointmentCard(null);
    setSelectedAppointment(null);
    setAppointmentModalOpen(false);
  };

  const handleOpenScheduleMessage = (card) => {
    setScheduleCard(card);
    setScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = () => {
    setScheduleCard(null);
    setScheduleModalOpen(false);
  };

  // Aviso de CNPJ ja' cadastrado (pedido real da planilha RRSTRIKE que
  // originou esta tela: "antes de incluir, consulte o CNPJ para evitar
  // duplicidade do mesmo problema"). So' avisa, nao bloqueia - o board de
  // "Clientes Criticos" pode ter mais de um caso legitimo pro mesmo CNPJ
  // em datas diferentes.
  const lastWarnedCnpjRef = useRef(null);
  const checkCnpjDuplicate = (def, rawValue) => {
    if (def.type !== "cnpj") return;
    const digits = (rawValue || "").replace(/\D/g, "");
    if (digits.length < 14 || digits === lastWarnedCnpjRef.current) return;

    const match = cards.find((c) =>
      (c.fieldValues || []).some(
        (fv) =>
          fv.customFieldDefinitionId === def.id &&
          (fv.value || "").replace(/\D/g, "") === digits
      )
    );
    if (match) {
      lastWarnedCnpjRef.current = digits;
      toast.warn(
        `Já existe um caso para este CNPJ: "${match.title || match.ticket?.contact?.name || "sem título"}".`
      );
    }
  };

  const handleNewCardFieldChange = (defId, value) => {
    setNewCardFieldValues((prev) => ({ ...prev, [defId]: value }));
    const def = customFieldDefinitions.find((d) => d.id === defId);
    if (def) checkCnpjDuplicate(def, value);
  };

  const handleCreateBoard = async () => {
    if (!newBoardName.trim()) return;
    try {
      const { data } = await api.post("/boards", { name: newBoardName });
      toast.success("Board criado!");
      setBoards((prev) => [...prev, data]);
      setActiveBoardId(data.id);
      setNewBoardOpen(false);
      setNewBoardName("");
    } catch (err) {
      toastError(err);
    }
  };

  const resetAddCardForm = () => {
    setAddCardOpen(false);
    setNewCardMode("ticket");
    setNewCardTicketId("");
    setNewCardTitle("");
    setNewCardPhaseId("");
    setNewCardResponsavelId("");
    setNewCardFieldValues({});
    setNewCardFiles([]);
    lastWarnedCnpjRef.current = null;
  };

  // Card "avulso" (newCardMode === "standalone"): nao veio de uma
  // conversa - pedido real do usuario 2026-08-27 ("nao posso ficar preso
  // em numero de ticket pra criar um card, quero subir um que nao veio de
  // conversa"). Usa "title" em vez de "ticketId" pra identificar o card.
  const handleAddCard = async () => {
    // Sem isso, cliques repetidos no "Adicionar" (ex: usuario achando que
    // nao funcionou, tentando de novo) criavam varios cards identicos -
    // card avulso nao tem ticketId pra checar duplicata como o modo
    // ticket ja checa. Bug real: 4 copias de "Casa do Acarejé" criadas
    // assim, 2026-08-28.
    if (savingNewCard) return;
    if (newCardMode === "ticket" && (!newCardTicketId || !newCardPhaseId)) return;
    if (newCardMode === "standalone" && (!newCardTitle.trim() || !newCardPhaseId)) return;

    setSavingNewCard(true);
    try {
      const { data: newCard } = await api.post("/cards", {
        boardId: activeBoardId,
        phaseId: newCardPhaseId,
        responsavelId: newCardResponsavelId || null,
        ...(newCardMode === "ticket"
          ? { ticketId: Number(newCardTicketId) }
          : { title: newCardTitle.trim() }),
      });

      const fieldsForPhase = customFieldDefinitions.filter(
        (def) => !def.phaseId || def.phaseId === Number(newCardPhaseId)
      );
      const filledFields = fieldsForPhase.filter((def) => newCardFieldValues[def.id]);
      if (filledFields.length > 0) {
        await Promise.all(
          filledFields.map((def) =>
            api.post("/card-field-values", {
              cardId: newCard.id,
              customFieldDefinitionId: def.id,
              value: newCardFieldValues[def.id],
            })
          )
        );
      }

      if (newCardFiles.length > 0) {
        const formData = new FormData();
        // typeArch/fileId dizem pro multer compartilhado (config/upload.ts)
        // pra salvar em public/company{id}/cardAttachments/{cardId}/ - sem
        // isso ele cai direto na raiz da empresa e o link de download do
        // BoardCardModal (que ja espera esse caminho) quebra.
        formData.append("typeArch", "cardAttachments");
        formData.append("fileId", String(newCard.id));
        newCardFiles.forEach((file) => formData.append("file", file));
        await api.post(`/cards/${newCard.id}/attachments`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      toast.success(
        newCardMode === "ticket" ? "Ticket adicionado ao board!" : "Card adicionado ao board!"
      );
      resetAddCardForm();
      fetchBoardDetail();
    } catch (err) {
      toastError(err);
    } finally {
      setSavingNewCard(false);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.toolbar}>
        <div className={classes.toolbarLeft}>
          <Select
            value={activeBoardId || ""}
            onChange={(e) => setActiveBoardId(e.target.value)}
            variant="outlined"
            displayEmpty
          >
            {boards.length === 0 && (
              <MenuItem value="">
                <em>Nenhum board</em>
              </MenuItem>
            )}
            {boards.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </Select>
          <IconButton size="small" onClick={() => setNewBoardOpen(true)}>
            <AddIcon />
          </IconButton>
          {activeBoardId && (
            <>
              <Can
                role={user.profile}
                perform="board-phases:manage"
                yes={() => (
                  <>
                    <Tooltip title="Mover board pra cima na lista">
                      <span>
                        <IconButton
                          size="small"
                          disabled={boards.findIndex((b) => b.id === activeBoardId) <= 0}
                          onClick={() => handleMoveBoardPosition("up")}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Mover board pra baixo na lista">
                      <span>
                        <IconButton
                          size="small"
                          disabled={boards.findIndex((b) => b.id === activeBoardId) >= boards.length - 1}
                          onClick={() => handleMoveBoardPosition("down")}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                )}
              />
              <Tooltip title="Regras de automação">
                <IconButton onClick={() => setRulesOpen(true)}>
                  <Badge badgeContent={rulesCount} color="primary">
                    <OfflineBolt />
                  </Badge>
                </IconButton>
              </Tooltip>
              <Can
                role={user.profile}
                perform="board-phases:manage"
                yes={() => (
                  <Tooltip title="Configurações do board">
                    <IconButton onClick={() => setSettingsOpen(true)}>
                      <SettingsIcon />
                    </IconButton>
                  </Tooltip>
                )}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setNewCardResponsavelId(user?.id || "");
                  setAddCardOpen(true);
                }}
              >
                Adicionar ticket
              </Button>
            </>
          )}
        </div>
      </div>

      {activeBoardId && (
        <div className={classes.boardContainer}>
          {isMobile ? (
            <>
              <div className={classes.mobilePhaseTabs}>
                {phases.map((phase) => {
                  const count = cards.filter((c) => c.phaseId === phase.id).length;
                  return (
                    <Chip
                      key={phase.id}
                      className={classes.mobilePhaseChip}
                      label={`${phase.name} (${count})`}
                      onClick={() => setActiveMobilePhaseId(phase.id)}
                      color={activeMobilePhaseId === phase.id ? "primary" : "default"}
                      variant={activeMobilePhaseId === phase.id ? "default" : "outlined"}
                    />
                  );
                })}
              </div>
              {mobilePhaseCards.length === 0 ? (
                <Box textAlign="center" mt={4}>
                  <Typography variant="body2" color="textSecondary">
                    Nenhum card nesta fase.
                  </Typography>
                </Box>
              ) : (
                mobilePhaseCards.map((card) => (
                  <MobileCardRow
                    key={card.id}
                    card={card}
                    onClick={() => handleCardClick(card.id)}
                    onSchedule={() => handleOpenAppointment(card)}
                    onScheduleMessage={() => handleOpenScheduleMessage(card)}
                  />
                ))
              )}
            </>
          ) : (
            <Board
              data={boardData}
              // "editable" liga o formulario nativo de "add card" do
              // react-trello ("Click to add card" no rodape de cada fase) -
              // esse formulario nunca foi conectado ao nosso backend (nao
              // manda ticketId nenhum) e o card fantasma que ele gera, sem
              // "ticket", quebrava a tela inteira ao renderizar no MyCard
              // (Tooltip do MUI nao aceita null como filho, e IconChannel
              // retorna null quando ticket.channel nao existe). O card real
              // sempre foi criado pelo botao "Adicionar ticket" no topo (que
              // de fato grava um Card ligado a um Ticket) - bug real
              // reportado 2026-08-27.
              canAddLanes={false}
              components={{ Card: MyCard, LaneHeader: MyLaneHeader }}
              onCardMoveAcrossLanes={handleCardMove}
              onCardClick={handleCardClick}
              style={{ backgroundColor: "rgba(252, 252, 252, 0.03)" }}
            />
          )}
          {phases.length === 0 && (
            <Box textAlign="center" mt={4}>
              <Typography variant="body2" color="textSecondary">
                Esse board ainda não tem fases — abra as configurações (engrenagem) pra criar a primeira.
              </Typography>
            </Box>
          )}
        </div>
      )}

      <BoardSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        boardId={activeBoardId}
        onChanged={fetchBoardDetail}
        onDeleted={handleBoardDeleted}
      />

      <BoardCardModal
        open={cardModalOpen}
        onClose={() => setCardModalOpen(false)}
        card={modalCard}
        customFieldDefinitions={customFieldDefinitions}
        phases={phases}
        onSaved={fetchBoardDetail}
      />

      <AppointmentModal
        open={appointmentModalOpen}
        onClose={handleCloseAppointmentModal}
        appointment={selectedAppointment}
        cardId={appointmentCard?.id}
        ticketId={appointmentCard?.ticket?.id}
        contactId={appointmentCard?.ticket?.contact?.id || appointmentCard?.contact?.id}
        reload={fetchBoardDetail}
      />

      {scheduleModalOpen && (
        <ScheduleModal
          open={scheduleModalOpen}
          onClose={handleCloseScheduleModal}
          scheduleId={null}
          contactId={scheduleCard?.ticket?.contact?.id || scheduleCard?.contact?.id}
          cleanContact={() => {}}
          reload={fetchBoardDetail}
        />
      )}

      <Dialog open={rulesOpen} onClose={() => setRulesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Regras de Automação</DialogTitle>
        <DialogContent dividers>
          {rules.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              Nenhuma regra configurada. Use a engrenagem &gt; Automação pra criar.
            </Typography>
          ) : (
            <List>
              {rules.map((r) => (
                <ListItem key={r.id}>
                  <ListItemIcon>
                    <OfflineBolt color={r.active ? "primary" : "disabled"} />
                  </ListItemIcon>
                  <ListItemText
                    primary={r.name || `${r.triggerType} = ${r.triggerValue}`}
                    secondary={`Mover para: ${r.targetPhase?.name || r.targetPhaseId}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRulesOpen(false)} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={newBoardOpen} onClose={() => setNewBoardOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Novo board</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Nome do board"
            fullWidth
            margin="dense"
            variant="outlined"
            autoFocus
            value={newBoardName}
            onChange={(e) => setNewBoardName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewBoardOpen(false)} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleCreateBoard} color="primary" variant="contained">
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addCardOpen} onClose={resetAddCardForm} maxWidth="xs" fullWidth>
        <DialogTitle>Adicionar card ao board</DialogTitle>
        <DialogContent dividers>
          <ButtonGroup fullWidth size="small" style={{ marginBottom: 12 }}>
            <Button
              variant={newCardMode === "ticket" ? "contained" : "outlined"}
              color={newCardMode === "ticket" ? "primary" : "default"}
              onClick={() => setNewCardMode("ticket")}
            >
              De uma conversa
            </Button>
            <Button
              variant={newCardMode === "standalone" ? "contained" : "outlined"}
              color={newCardMode === "standalone" ? "primary" : "default"}
              onClick={() => setNewCardMode("standalone")}
            >
              Avulso
            </Button>
          </ButtonGroup>

          {newCardMode === "ticket" ? (
            <TextField
              label="Número do ticket"
              type="number"
              fullWidth
              margin="dense"
              variant="outlined"
              value={newCardTicketId}
              onChange={(e) => setNewCardTicketId(e.target.value)}
            />
          ) : (
            <TextField
              label="Título"
              fullWidth
              margin="dense"
              variant="outlined"
              placeholder="Ex: Lead da feira, Indicação da Maria..."
              value={newCardTitle}
              onChange={(e) => setNewCardTitle(e.target.value)}
            />
          )}
          <TextField
            label="Fase"
            select
            fullWidth
            margin="dense"
            variant="outlined"
            value={newCardPhaseId}
            onChange={(e) => {
              setNewCardPhaseId(e.target.value);
              setNewCardFieldValues({});
            }}
          >
            {phases.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Responsável"
            select
            fullWidth
            margin="dense"
            variant="outlined"
            value={newCardResponsavelId}
            onChange={(e) => setNewCardResponsavelId(e.target.value)}
          >
            <MenuItem value="">
              <em>Sem responsável</em>
            </MenuItem>
            {users.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.name}
              </MenuItem>
            ))}
          </TextField>
          {newCardPhaseId &&
            customFieldDefinitions
              .filter((def) => !def.phaseId || def.phaseId === Number(newCardPhaseId))
              .map((def) => (
                <CardFieldInput
                  key={def.id}
                  def={def}
                  value={newCardFieldValues[def.id]}
                  onChange={(value) => handleNewCardFieldChange(def.id, value)}
                />
              ))}

          <Box mt={1}>
            <input
              id="new-card-file-input"
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={(e) => setNewCardFiles(Array.from(e.target.files || []))}
            />
            <label htmlFor="new-card-file-input">
              <Button component="span" size="small" startIcon={<AttachFileIcon />}>
                {newCardFiles.length > 0
                  ? `${newCardFiles.length} arquivo(s) selecionado(s)`
                  : "Anexar arquivo (opcional)"}
              </Button>
            </label>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAddCardForm} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleAddCard} color="primary" variant="contained" disabled={savingNewCard}>
            {savingNewCard ? "Adicionando..." : "Adicionar"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Boards;
